import { useThemeColors } from '@/styles';
import { Icon, IconName } from '@/icons';

export interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Fires on every click, even when the input is already focused (onFocus won't). */
  onClick?: () => void;
  /** Keyboard handler for the underlying input (e.g. Enter/Escape in a combobox). */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Extra attributes for the underlying input (e.g. ARIA combobox props).
   * Props with a dedicated SearchInput prop can't be passed here.
   */
  inputProps?: Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    | 'value'
    | 'onChange'
    | 'onFocus'
    | 'onBlur'
    | 'onClick'
    | 'onKeyDown'
    | 'placeholder'
    | 'style'
    | 'className'
  >;
}

/**
 * Text input for filtering an already-fetched list client-side. Pair with
 * `useLocalFilter` — no separate search API call needed.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  style,
  onFocus,
  onBlur,
  onClick,
  onKeyDown,
  inputProps,
}: SearchInputProps) {
  const colors = useThemeColors();

  return (
    <div className={className} style={{ position: 'relative', ...style }}>
      <Icon
        name={IconName.SEARCH}
        size="sm"
        color={colors.text.tertiary}
        style={{
          position: 'absolute',
          left: '0.625rem',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
        }}
      />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
        onKeyDown={onKeyDown}
        {...inputProps}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem 0.5rem 2rem',
          borderRadius: '0.375rem',
          border: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background,
          color: colors.text.primary,
          fontSize: '0.875rem',
          outline: 'none',
        }}
      />
    </div>
  );
}
